import { Checkbox, IconButton, Popover, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowUp, Add } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import LoadingIndicator from 'components/LoadingIndicator';
import { Delete as DeleteIcon, Edit as EditIcon } from '@material-ui/icons';

import _ from 'lodash';
import useDialog from 'components/ConfirmDialog/useDialog';
import { useDispatch, useSelector } from 'react-redux';
import TableHeader from './TableHeader';
import { generateRandomId, produce } from 'utility/general';
import useMeasure from 'hooks/useMeasure';
import usePrevious from 'hooks/usePrevious';
import { checkNull } from 'utility/formValidate';
import { setTableUI } from 'store/table/reducer';
import { TABLE_ROW_REMOVE } from 'utility/frontendMessage';

const SLayout = styled.div`
    width: 100%;
    box-shadow: ${(props) => (props.fullWidthTable ? 'rgba(99, 99, 99, 0.2) 0px 2px 4px 0px' : 'none')};
`;

const SNormal = styled.div``;

const SDotStyle = styled.div`
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    overflow: hidden;
    white-space: no-wrap;
    text-overflow: ellipsis;
`;

const SContentLayout = styled.div`
    position: absolute;
    display: flex;
    top: 0;
    bottom: 0;
    left: 50%;
    align-items: center;
    transform: translateX(-50%);
`;

const SDummy = styled.div``;

const useStyles = makeStyles((theme) => ({
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        padding: theme.spacing(1),
    },
}));

const SValueCellStyle = styled.div`
    width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid #dcdcdc;
    border-top: ${(props) => (props.shouldShowTopBorder ? '1px solid #dcdcdc' : 'none')};
    flex-shrink: 0;
`;

const SLoadingIndicator = styled.div`
    position: absolute;
    left: ${(props) => props.left ?? 0}px;
    top: 0;
    width: ${(props) => props.width ?? 0}px;
    bottom: 0;
    background: rgba(128, 128, 128, 0.3);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CustomTableRow = ({
    rowIndex,
    columns,
    row,
    isCheckboxEnable,
    isExpandRow,
    shouldToggle,
    borderColor,
    onExpandToggle = () => null,
    shouldShowTopBorder,
    checkboxStatus,
    getFieldWidth = () => null,
    onCheckboxSelected = () => null,
    textAlign,
    fullWidthTable,
    isDeleteAllow,
    isEditAllow,
    onEdit = (row) => null,
    showAlternativeBackground = false,
    disableOldDataDelete = false,
    disableOldDataEdit,
    onDelete = (row, rowIndex) => null,
    disabled = false,
    parentWidth,
    disableDeleteConfirm,
    disabledCheckbox,
    applicationCreateDtm,
    test,
    isRowNumberEnabled = false,
    indexAddDot = false,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentHoverColumn, setCurrentHoverColumn] = useState('');
    const hoverStyles = useStyles();
    const showDialog = useDialog();

    const handlePopoverOpen = (event, type) => {
        setCurrentHoverColumn(type);
        setAnchorEl(event.currentTarget);
    };

    const isCheckboxCheck = (row) => {
        return checkboxStatus.find((item) => item.id === row.id && item.docType === row.docType);
    };

    const onCheckboxChange = (row) => {
        onCheckboxSelected(row);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const getRowValue = (rowKey, rowValue, defaultValue) => {
        const currentColumn = columns.find((column) => {
            return column.field === rowKey;
        });

        if (currentColumn.valueGetter) {
            return currentColumn.valueGetter(
                {
                    row,
                    value: rowValue,
                    getValue: (columnKey) => {
                        return row[columnKey];
                    },
                },
                rowIndex,
            );
        }

        if (currentColumn.renderCell) {
            return currentColumn.renderCell({
                row,
                value: rowValue,
                getValue: (columnKey) => {
                    return row[columnKey];
                },
            });
        }

        if (checkNull(rowValue)) {
            return '-';
        }

        if (!rowValue && defaultValue) {
            return defaultValue;
        }

        if (rowValue === true) {
            return 'Yes';
        } else if (rowValue === false) {
            return 'No';
        }

        if (checkNull(rowValue)) {
            return '-';
        }

        return rowValue;
    };

    const getSubTable = () => {
        const subTableObject = columns.find((item) => {
            return item.field === 'subTable';
        });

        return subTableObject?.subTable;
    };

    const getAlternativeColor = (index, fallbackColor = 'initial') => {
        if (index % 2 === 1 && showAlternativeBackground) {
            return '#f1f1f1';
        } else {
            return fallbackColor;
        }
    };

    const getIsToggleDisabled = (rowIndex) => {
        const subTableObject = columns.find((item) => {
            return item.field === 'subTable';
        });

        return !subTableObject?.getIsToggleEnable({ row });
    };

    const getShouldShowTopBorder = () => {
        if (shouldShowTopBorder) {
            return true;
        }
    };

    const getDeleteButtonDisable = (row) => {
        if (disableOldDataDelete) {
            if (new Date(row.createDtm).getTime() < new Date(applicationCreateDtm).getTime() + 5000) {
                return true;
            }
        }

        if (row.oldData) {
            return true;
        }

        return disabled ? disabled : row.disableRowDelete;
    };

    const getEditButtonDisable = (row) => {
        return disabled || !!row.endEffectiveDate || disableOldDataEdit;
    };

    const SubTable = getSubTable();

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    borderLeft: '1px solid #dcdcdc',
                    position: 'relative',
                    fontSize: 14,
                }}
            >
                {isRowNumberEnabled && (
                    <SValueCellStyle
                        shouldShowTopBorder={getShouldShowTopBorder()}
                        style={{
                            background: getAlternativeColor(rowIndex),
                            borderRight: `1px solid #e8e8e8`,
                        }}
                    >
                        {rowIndex+1}{indexAddDot ? '.': ''}
                    </SValueCellStyle>
                )}
                {isExpandRow && (
                    <SValueCellStyle
                        shouldShowTopBorder={getShouldShowTopBorder()}
                        style={{
                            background: getAlternativeColor(rowIndex),
                            borderRight: `1px solid #e8e8e8`,
                        }}
                    >
                        <IconButton
                            size={'small'}
                            disabled={row?.isFreeze || getIsToggleDisabled()}
                            onClick={() => {
                                if (getIsToggleDisabled()) {
                                    return;
                                }

                                onExpandToggle(row.id, !shouldToggle);
                            }}
                        >
                            {getIsToggleDisabled() ? (
                                <Add style={{ color: 'rgba(0, 0, 0, 0.26)' }} />
                            ) : shouldToggle ? (
                                <KeyboardArrowUp />
                            ) : (
                                <Add style={{ color: getIsToggleDisabled() ? 'rgba(0, 0, 0, 0.26)' : '' }} />
                            )}
                        </IconButton>
                    </SValueCellStyle>
                )}

                {isEditAllow && (
                    <SValueCellStyle
                        shouldShowTopBorder={getShouldShowTopBorder()}
                        style={{
                            background: getAlternativeColor(rowIndex),
                            borderRight: `1px solid #e8e8e8`,
                        }}
                    >
                        <IconButton
                            size={'small'}
                            disabled={getEditButtonDisable(row)}
                            onClick={async () => {
                                onEdit(row);
                            }}
                        >
                            <EditIcon
                                color="primary"
                                fontSize="small"
                                style={{
                                    fontSize: 17,
                                    color: getEditButtonDisable(row) ? '#dcdcdc' : '#3F51B5',
                                }}
                            />
                        </IconButton>
                    </SValueCellStyle>
                )}
                {isCheckboxEnable && (
                    <SValueCellStyle
                        shouldShowTopBorder={getShouldShowTopBorder()}
                        style={{
                            background: getAlternativeColor(rowIndex),
                            borderRight: `1px solid #e8e8e8`,
                        }}
                    >
                        <Checkbox
                            color="primary"
                            onChange={() => onCheckboxChange(row)}
                            checked={isCheckboxCheck(row)}
                            disabled={
                                isCheckboxCheck(row)
                                    ? false
                                    : row.disabledItemCheckbox
                                    ? row.disabledItemCheckbox
                                    : disabledCheckbox
                            }
                        ></Checkbox>
                    </SValueCellStyle>
                )}

                {columns
                    .filter((item) => !item.hiddenInTableColumn)
                    .map((item, index) => {
                        const isLastIndex = columns.filter((item) => !item.hiddenInTableColumn).length - 1 === index;

                        const extraProps = {};
                        const extraStyle = {};
                        const valueProps = {};
                        const valueStyle = {};

                        const HoverComponent = item.hoverComponent;

                        if (item.goToPath) {
                            valueProps.onClick = () => item.goToPath({ row });
                            valueStyle.cursor = 'pointer';
                            valueStyle.color = '#0079bf';
                        }

                        if (item.hoverComponent) {
                            extraProps.onMouseEnter = (e) => handlePopoverOpen(e, item.field);
                            extraProps.onMouseLeave = handlePopoverClose;
                        }

                        if (item.dotNotion) {
                            extraStyle.overflow = 'hidden';
                            extraStyle.whiteSpace = 'noWrap';
                            extraStyle.textOverflow = 'ellipsis';
                            extraStyle.width = getFieldWidth(item.field);
                            extraStyle.top = '50%';
                        }

                        const Wrapper = item.dotNotion ? SDotStyle : SNormal;

                        if (!item.textNotWrap) {
                            valueStyle.whiteSpace = 'pre-line';
                        }

                        return (
                            <div
                                style={{
                                    background: getAlternativeColor(rowIndex),
                                    width: getFieldWidth(item.field),
                                    flexGrow: fullWidthTable && !item.fixWidth ? 1 : 0,
                                    flexShrink: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: 35,
                                    borderTop: getShouldShowTopBorder() ? '1px solid #dcdcdc' : 'none',
                                    borderBottom: '1px solid #dcdcdc',
                                    // borderRight: '1px solid #dcdcdc',
                                    borderRight: `1px solid ${isLastIndex ? '#dcdcdc' : '#E8E8E8'}`,
                                    textAlign: 'center',
                                }}
                                key={item.field}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        textAlign: textAlign ? textAlign : 'center',
                                    }}
                                >
                                    <div {...extraProps} style={{ ...extraStyle }}>
                                        {item.hoverComponent && item.dotNotion && (
                                            <div style={{ opacity: 0 }}>text placeholder</div>
                                        )}

                                        <Wrapper style={{ padding: '6px 10px' }}>
                                            <span
                                                {...valueProps}
                                                style={{
                                                    ...valueStyle,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {getRowValue(item.field, row[item.field], item.defaultValue)}
                                            </span>
                                        </Wrapper>
                                    </div>
                                </div>

                                {item.hoverComponent && (
                                    <Popover
                                        className={hoverStyles.popover}
                                        classes={{
                                            paper: hoverStyles.paper,
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        anchorEl={anchorEl}
                                        open={!!anchorEl && item.field === currentHoverColumn}
                                        disableRestoreFocus
                                        disableScrollLock={false}
                                        onClose={handlePopoverClose}
                                    >
                                        <HoverComponent row={row} />
                                    </Popover>
                                )}
                            </div>
                        );
                    })}

                {isDeleteAllow && (
                    <SValueCellStyle
                        shouldShowTopBorder={getShouldShowTopBorder()}
                        style={{
                            background: getAlternativeColor(rowIndex),
                            borderRight: `1px solid #e8e8e8`,
                        }}
                    >
                        <IconButton
                            size={'small'}
                            disabled={getDeleteButtonDisable(row)}
                            onClick={async () => {
                                if (!disableDeleteConfirm) {
                                    const isConfirm = await showDialog({
                                        description: TABLE_ROW_REMOVE,
                                    });

                                    if (!isConfirm) {
                                        return;
                                    }
                                }

                                onDelete(row, rowIndex);
                            }}
                        >
                            <DeleteIcon
                                color={getDeleteButtonDisable(row) ? 'disabled' : 'primary'}
                                fontSize="small"
                                style={{ fontSize: 17 }}
                            />
                        </IconButton>
                    </SValueCellStyle>
                )}
            </div>
            {shouldToggle && SubTable && !getIsToggleDisabled() && (
                <div
                    style={{
                        borderLeft: '1px solid #dcdcdc',
                        position: 'relative',
                    }}
                >
                    <div
                        style={{
                            width: parentWidth,
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                        }}
                    />
                    <SubTable row={row} />
                </div>
            )}
        </div>
    );
};

const EmptyRow = ({ children, parentWidth, tableUid }) => {
    const scrollLeft = useSelector((state) => state.table[`table-${tableUid}-scrollLeft`]);
    return (
        <div style={{ position: 'relative', height: 80 }}>
            <div
                style={{
                    position: 'absolute',
                    left: scrollLeft ?? 0,
                    bottom: 0,
                    height: 80,
                    width: parentWidth,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <SContentLayout>
                    <div>
                        <ReportProblemIcon style={{ color: '#808080' }} />
                    </div>
                    <div style={{ marginLeft: 20 }}>No Data</div>
                </SContentLayout>
            </div>
        </div>
    );
};

const TableLoading = ({ parentWidth, tableUid }) => {
    const scrollLeft = useSelector((state) => state.table[`table-${tableUid}-scrollLeft`]);
    const scrollHeight = useSelector((state) => state.table[`table-${tableUid}-scrollHeight`]);

    return (
        <SLoadingIndicator
            style={{ transform: `translateY(${scrollHeight}px)` }}
            width={parentWidth}
            left={scrollLeft ?? 0}
            top={scrollHeight ?? 0}
        >
            <LoadingIndicator />
        </SLoadingIndicator>
    );
};

const CustomTable = ({
    isDesc = true,
    columns = [],
    rows = [],
    isExpandRow,
    isCheckboxEnable,
    headerBackground = '#e7e7ec',
    headerColor = '#272727',
    allowResize = false,
    resizeType = 'application',
    borderColor = '#dcdcdc',
    isExpandAll = false,
    onRowSelected = () => null,
    isScrollable = true,
    isEditAllow = false,
    onEdit = (row) => null,
    showAlternativeBackground = true,
    onChange = (rows) => null,
    textAlign,
    fullWidthTable = true,
    style = {},
    isSortable = true,
    defaultSortKey = '',
    defaultSortOnFrontend = false,
    isDeleteAllow = false,
    onDelete = (row, rowIndex) => null,
    maxHeight = 550,
    isSubTable = false,
    skipMaxHeight = true,
    loading = false,
    disabled = false,
    allowAction = true,
    disableOnPermission = false,
    name = `table_${generateRandomId()}`,
    onAppFieldValidate,
    disableDeleteConfirm = false,
    onDisableChange = (disable) => null,
    disabledHeaderCheckbox,
    disabledItemCheckbox,
    disableOldDataDelete = false,
    disableOldDataEdit,
    disableTableBorder = false,
    applicationCreateDtm,
    showGroupTab = false, // only for application study article license table
    test,
    isRowNumberEnabled = false,
    indexAddDot = false,
    indexHeaderName = "",
}) => {
    const containerRef = useRef();
    const [headerWidth, setHeaderWidth] = useState(0);
    const [isAllCheckboxSelect, setAllCheckboxSelect] = useState(false);
    const [checkboxStatus, setCheckboxStatus] = useState([]);
    const dispatch = useDispatch();
    const [uid, { width: parentWidth }] = useMeasure();
    const [tableUid] = useState(generateRandomId());
    const [sortParams, setSortParams] = useState({ key: defaultSortKey, isDesc, isServer: false });
    const previousRows = usePrevious(rows);

    useEffect(() => {
        setSortParams({ key: defaultSortKey, isDesc, isServer: !defaultSortOnFrontend });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesc, defaultSortKey]);

    useEffect(() => {
        if (previousRows) {
            if (JSON.stringify(previousRows) !== JSON.stringify(rows)) {
                onChange(rows);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    useEffect(() => {
        onRowSelected(
            rows?.filter((item) => {
                return checkboxStatus.some(
                    (checkboxItem) => checkboxItem.id === item.id && checkboxItem.docType === item.docType,
                );
            }),
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkboxStatus]);

    useEffect(() => {
        if (onAppFieldValidate) {
            // if table is disabled, just return validation pass

            if (getDisableStatus() === true) {
                // checking for preventing dead loop
                if (JSON.stringify(rows) !== JSON.stringify(previousRows)) {
                    onAppFieldValidate(name, true);
                }
            } else {
                if (previousRows?.length === 0 && rows?.length > 0) {
                    onAppFieldValidate(name, true);
                }

                if (previousRows?.length > 0 && rows?.length === 0) {
                    onAppFieldValidate(name, false);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    // onChange when disabled status update
    useEffect(() => {
        const isDisabled = getDisableExceptionPermission();

        if (onAppFieldValidate) {
            if (isDisabled) {
                onAppFieldValidate(name, true);
            } else {
                const isDataExist = rows.length > 0;
                onAppFieldValidate(name, isDataExist);
            }
        }

        onDisableChange(isDisabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, allowAction]);

    const onCheckboxSelected = (row) => {
        const newCheckboxStatus = produce(checkboxStatus, (draft) => {
            const index = draft.findIndex((item) => {
                return item.id === row.id && item.docType === row.docType;
            });

            if (index === -1) {
                draft.push({ id: row.id, docType: row.docType });
            } else {
                draft.splice(index, 1);
            }
        });

        setCheckboxStatus(newCheckboxStatus);
        setAllCheckboxSelect(newCheckboxStatus.length === rows.length);
    };

    const getInitialColumnWidth = () => {
        return columns.map((item) => {
            return {
                width: item.width,
                field: item.field,
            };
        });
    };

    const [toggleStatus, setToggleStatus] = useState([]);
    const [columnWidths, setColumnWidth] = useState(getInitialColumnWidth);

    // when columns changed, the column width array needs to be updated
    useEffect(() => {
        setColumnWidth(
            produce((draft) => {
                columns.forEach((item) => {
                    const index = draft.findIndex((column) => column.field === item.field);
                    if (index === -1) {
                        draft.push({ width: item.width, field: item.field });
                    }
                });
            }),
        );
    }, [columns]);

    useEffect(() => {
        const newStatus = rows?.map((row) => {
            if (isExpandAll > 0) {
                const isToggleEnable = columns
                    .find((item) => {
                        return item.field === 'subTable';
                    })
                    ?.getIsToggleEnable({ row });

                return {
                    id: row.id,
                    isToggle: isToggleEnable,
                };
            }

            return {
                id: row.id,
                isToggle: false,
            };
        });

        setToggleStatus(newStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpandAll]);

    // reinitialize the toggle status if rows changed
    useEffect(() => {
        const updatedToggleStatus = rows?.map((item) => {
            return {
                id: item.id,
                isToggle: false,
            };
        });

        setToggleStatus(updatedToggleStatus);
    }, [rows]);

    const toggleAllCheckbox = () => {
        if (isAllCheckboxSelect) {
            setCheckboxStatus([]);
            setAllCheckboxSelect(false);
        } else {
            const newCheckbox = rows.map((item) => {
                return { id: item.id, docType: item.docType };
            });
            setCheckboxStatus(newCheckbox);
            setAllCheckboxSelect(true);
        }
    };

    const getFieldWidth = (field) => {
        return columnWidths.find((item) => item.field === field)?.width || 100;
    };

    const getSortData = (data) => {
        if (sortParams.isServer) {
            return data;
        }

        if (!sortParams.key) {
            return data;
        } else {
            const column = columns.find((item) => item.field === sortParams.key);

            let sortData = data;
            let sortKey = sortParams.key;

            // if sortLookup exist, sorting with lookup order instead of enum value
            if (column?.sortLookup) {
                sortData = sortData.map((item) => {
                    item.customSort = column.sortLookup[item[sortParams.key]];
                    return item;
                });

                sortKey = 'customSort';
            }

            const newData = _.orderBy(sortData, [sortKey], [sortParams.isDesc ? 'desc' : 'asc']);
            return newData;
        }
    };

    const getHeaderWidth = () => {
        if (headerWidth < parentWidth) {
            return headerWidth;
        } else {
            return parentWidth;
        }
    };

    const getDisableExceptionPermission = () => {
        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getDisableStatus = () => {
        if (disableOnPermission) {
            return true;
        }

        if (disabled) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getColumnWidth = (key) => {
        return document.querySelector(`#${key}`)?.clientWidth ?? 0;
    };

    const getTableGroupHeader = () => {
        if (showGroupTab) {
            const startOfSame =
                getColumnWidth(name + '_articleControl') +
                getColumnWidth(name + '_type') +
                getColumnWidth(name + '_name') +
                getColumnWidth(name + '_edit');

            const endOfSame =
                getColumnWidth(name + '_sameHK') +
                getColumnWidth(name + '_sameOversea') +
                getColumnWidth(name + '_sameCountry');

            const widthOfOther =
                getColumnWidth(name + '_otherHK') +
                getColumnWidth(name + '_otherOversea') +
                getColumnWidth(name + '_otherCountry');

            return (
                <>
                    <div
                        style={{
                            position: 'absolute',
                            left: startOfSame + 4,
                            top: 0,
                            height: 28,
                            width: endOfSame + 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #c5c5c5',
                            background: '#E7E7EC',
                        }}
                    >
                        Same indication registration
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            left: startOfSame + endOfSame + 7,
                            top: 0,
                            height: 28,
                            width: widthOfOther + 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #c5c5c5',
                            background: '#E7E7EC',
                        }}
                    >
                        Other indication registration
                    </div>
                </>
            );
        } else {
            return null;
        }
    };

    const extraProps = {};

    if (!isSubTable && !skipMaxHeight) {
        extraProps.maxHeight = maxHeight;
    }

    let Wrapper = disableTableBorder ? SDummy : Paper;

    return (
        <SLayout className={uid} style={{ ...style }} fullWidthTable={fullWidthTable}>
            <div style={{ width: 1 }}>
                <Wrapper style={{ width: headerWidth < parentWidth ? headerWidth : parentWidth }}>
                    <div
                        ref={containerRef}
                        style={{
                            ...extraProps,
                            width: parentWidth,
                            overflowX: isScrollable ? 'auto' : 'initial',
                            position: 'relative',
                        }}
                        onScroll={() => {
                            dispatch(
                                setTableUI({
                                    [`table-${tableUid}-scrollHeight`]: containerRef.current.scrollTop,
                                    [`table-${tableUid}-scrollLeft`]: containerRef.current.scrollLeft,
                                }),
                            );
                        }}
                    >
                        {showGroupTab && getTableGroupHeader()}
                        <TableHeader
                            name={name}
                            tableUid={tableUid}
                            isEditAllow={isEditAllow}
                            isSubTable={isSubTable}
                            isExpandRow={isExpandRow}
                            isCheckboxEnable={isCheckboxEnable}
                            disabledCheckbox={disabledHeaderCheckbox}
                            headerColor={headerColor}
                            borderColor={borderColor}
                            headerBackground={headerBackground}
                            columns={columns}
                            isDeleteAllow={isDeleteAllow}
                            toggleAllCheckbox={toggleAllCheckbox}
                            isAllCheckboxSelect={isAllCheckboxSelect}
                            isSortable={isSortable}
                            textAlign={textAlign}
                            fullWidthTable={fullWidthTable}
                            allowResize={allowResize}
                            getFieldWidth={getFieldWidth}
                            setColumnWidth={setColumnWidth}
                            sortParams={sortParams}
                            setSortParams={setSortParams}
                            onTableHeaderWidthChange={(width) => setHeaderWidth(width)}
                            rows={rows}
                            showGroupTab={showGroupTab}
                            isRowNumberEnabled={isRowNumberEnabled}
                            indexHeaderName={indexHeaderName}
                        />
                        {getSortData(rows)?.length === 0 && (
                            <EmptyRow borderColor={borderColor} parentWidth={getHeaderWidth()} tableUid={tableUid} />
                        )}
                        <div>
                            {getSortData(rows)?.map((row, index) => {
                                const rowId = row.id;
                                const shouldToggle = toggleStatus.find((item) => item.id === rowId)?.isToggle;
                                const previousToggleId = getSortData(rows)?.[index - 1]?.id;
                                const previousToggleStatus = toggleStatus?.find(
                                    (item) => item.id === previousToggleId,
                                )?.isToggle;
                                const shouldShowTopBorder = previousToggleStatus;

                                return (
                                    <div key={`${row.id}-${row?.docType ?? index}-${row.documentType ?? index}`}>
                                        <CustomTableRow
                                            applicationCreateDtm={applicationCreateDtm}
                                            disableDeleteConfirm={disableDeleteConfirm}
                                            disabled={getDisableStatus()}
                                            showAlternativeBackground={showAlternativeBackground}
                                            rowIndex={index}
                                            disableOldDataDelete={disableOldDataDelete}
                                            disableOldDataEdit={disableOldDataEdit}
                                            fullWidthTable={fullWidthTable}
                                            textAlign={textAlign}
                                            isCheckboxEnable={isCheckboxEnable}
                                            disabledCheckbox={disabledItemCheckbox}
                                            checkboxStatus={checkboxStatus}
                                            onCheckboxSelected={onCheckboxSelected}
                                            shouldShowTopBorder={shouldShowTopBorder}
                                            isExpandRow={isExpandRow}
                                            borderColor={borderColor}
                                            key={row.id}
                                            row={row}
                                            shouldToggle={shouldToggle}
                                            columns={columns}
                                            getFieldWidth={getFieldWidth}
                                            isEditAllow={isEditAllow}
                                            onEdit={onEdit}
                                            isDeleteAllow={isDeleteAllow}
                                            onDelete={onDelete}
                                            parentWidth={containerRef.current?.scrollWidth - 2 ?? 0}
                                            onExpandToggle={(id, isExpand) => {
                                                setToggleStatus(
                                                    produce((draft) => {
                                                        const index = draft.findIndex((item) => item.id === id);
                                                        if (index !== -1) {
                                                            draft[index].isToggle = isExpand;
                                                        }
                                                    }),
                                                );
                                            }}
                                            isRowNumberEnabled={isRowNumberEnabled}
                                            indexAddDot={indexAddDot}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {loading && <TableLoading tableUid={tableUid} parentWidth={getHeaderWidth()} />}
                    </div>
                </Wrapper>
            </div>
        </SLayout>
    );
};

export default CustomTable;
